<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { PlayerPrize } from '@/types/types';
import { pauseGame, resumeGame } from '@/utils/GameHelper';
import { useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
// import IconQuestion from '@/components/icons/IconQuestion.vue';
import { createCurrencyFormatter } from '@/utils/formatCurrency';
import { options } from '@/core/Options';
import { campaign } from '@/core/Model';

const { t } = useI18n();

const emit = defineEmits<{
	(e: 'confirmPlayerWin'): void;
}>();

const props = defineProps<{
	playerWin: PlayerPrize | null;
	campaingTitle: string;
	skin: string; //default value is default))
}>();

const { formatShortCents, formatShortLocalCents } = createCurrencyFormatter({
	code: options?.rules?.currency?.code,
	symbol: options?.rules?.currency?.symbol,
	subunits: options?.rules?.currency?.subunits
});

const popupContarerRef = useTemplateRef<HTMLDivElement>('popupContarerRef');

const backgroundImageUrl = computed(() => {
	const skin = props.skin || 'default';
	// console.log('import.meta.env.VITE_APP_SKINS_PATH: ', import.meta.env.VITE_APP_SKINS_PATH, skin);
	return `${import.meta.env.VITE_APP_SKINS_PATH}/${skin}/bg.png`;
});

const confirmPlayerWin = () => {
	// console.log('confirmPlayerWin');
	emit('confirmPlayerWin');
};

// Handle clicks outside the modal content
const handleOutsideClick = (event: MouseEvent) => {
	if (popupContarerRef.value && !popupContarerRef.value.contains(event.target as Node)) {
		confirmPlayerWin();
	}
};

const isSingleCurrency = computed(() => campaign.value?.campaign_currency !== 'EUR');
const isShowLocal = computed(() => campaign.value?.campaign_currency !== options?.currency);

// Format the prize amount for display
const prize = computed(() =>
	isSingleCurrency.value
		? formatShortLocalCents(props.playerWin?.amount_cents_in_player_currency || 0)
		: formatShortCents(props.playerWin?.amount_cents_in_campaign_currency || 0)
);
const prizeLocal = computed(() => formatShortLocalCents(props.playerWin?.amount_cents_in_player_currency || 0));

// Format the prize amount for popover
//const formattedPrizePopover = computed(() => formatLocalCents(props.playerWin?.amount_cents_in_player_currency || 0));

// const isTooltipOpen = ref(false);
// const handleTooltipClick = (event: MouseEvent) => {
// 	event.stopPropagation();
// 	isTooltipOpen.value = !isTooltipOpen.value;
// };

onMounted(() => {
	pauseGame();
	window.addEventListener('mouseup', handleOutsideClick);
	// Trigger animation after a small delay
	// setTimeout(() => {
	// 	isVisible.value = true;
	// }, 100);
});

onUnmounted(() => {
	resumeGame();
	window.removeEventListener('mouseup', handleOutsideClick);
});
</script>

<template>
	<!-- modal with transition -->

	<!-- <div
		v-if="playerWin"
		class="fixed inset-0 z-[998] flex items-center justify-center overflow-auto bg-black bg-opacity-80 font-sans"
	> -->
	<div class="fixed inset-0 z-[998] flex items-center justify-center overflow-auto bg-black bg-opacity-80 font-sans">
		<!-- content with transition -->
		<div class="viewport flex min-h-full items-center justify-center py-4">
			<Transition name="fade">
				<div ref="popupContarerRef" class="popup-container mx-auto">
					<!-- Background Image -->
					<div
						class="relative h-full w-full overflow-hidden rounded-3xl bg-cover bg-center bg-no-repeat"
						:style="{ backgroundImage: `url(${backgroundImageUrl})` }"
					>
						<div class="flex h-full flex-col items-center justify-center p-6 text-center">
							<!-- Close button with transition -->
							<Transition name="fade-delayed">
								<button
									class="absolute right-4 top-4 cursor-pointer text-4xl text-white text-opacity-80"
									@click="confirmPlayerWin"
								>
									&times;
								</button>
							</Transition>

							<!-- YOU WON Title with transition -->
							<Transition name="slide-down">
								<h1 class="won-text uppercase">{{ t('win.win_label') }}</h1>
							</Transition>

							<!-- Prize Amount with transition -->
							<Transition name="slide-up">
								<div class="prize-text flex flex-col items-center">
									<div class="flex items-baseline gap-1 text-white">
										<span class="eur-value">{{ prize.split(' ')[0] }}</span>
										<span class="eur-eur">{{ prize.split(' ')[1] }}</span>
									</div>

									<template v-if="isShowLocal">
										<p class="local-currency">{{ prizeLocal }}</p>
										<p class="local-currency-tip">{{ t('win.minded_tip') }}</p>
									</template>
								</div>
							</Transition>

							<!-- Payment text with transition -->
							<Transition name="fade-delayed">
								<p class="prize-tip-text">{{ t('win.payment_time_tip') }}</p>
							</Transition>

							<!-- Claim Button with transition -->
							<Transition name="scale-up">
								<button @click="confirmPlayerWin" class="conf-btn">{{ t('win.continue_btn') }}</button>
							</Transition>

							<!-- Campaign Title with transition -->
							<Transition name="fade-delayed">
								<div class="campaign-title">{{ campaingTitle }}</div>
							</Transition>

							<!-- Logo with transition -->
							<Transition name="fade-delayed">
								<div class="logo mt-2">
									<img src="@/assets/win/logo.png" alt="Logo" class="h-6" />
								</div>
							</Transition>
						</div>
					</div>
				</div>
			</Transition>
		</div>
	</div>
</template>

<style scoped>
/* Modal fade transition */
/* .modal-fade-enter-active,
.modal-fade-leave-active {
	transition: opacity 4s ease-in-out;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
} */

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

/* Viewport container with scrollbar styling */
.viewport {
	width: calc(324px + 16px); /* Increased width for scrollbar + padding */
	max-height: 100vh;
	display: flex;
	align-items: flex-start; /* Changed from center to flex-start */
	justify-content: center;
	overflow-y: auto;

	/* Custom scrollbar styling */
	scrollbar-width: thin;
	scrollbar-color: rgba(255, 255, 255, 0.5) rgba(0, 0, 0, 0.2);
}

/* Custom scrollbar for webkit browsers (Chrome, Safari, newer Edge) */
.viewport::-webkit-scrollbar {
	width: 10px;
}

.viewport::-webkit-scrollbar-track {
	background: rgba(0, 0, 0, 1);
	border-radius: 10px;
}

.viewport::-webkit-scrollbar-thumb {
	background: rgba(255, 255, 255, 0.5);
	border-radius: 8px;
	transition: background 0.3s ease;
}

.viewport::-webkit-scrollbar-thumb:hover {
	background: rgba(255, 255, 255, 0.7);
	width: 8px;
}

/* Responsive popup container */
.popup-container {
	width: 324px;
	height: 504px;
	max-height: 90vh;
	margin-top: auto; /* Add auto margin to top */
	margin-bottom: auto; /* Add auto margin to bottom */

	/* Base size (400px and less) */
	min-width: 324px;
	min-height: 504px;
	position: relative;
}

/* Element-specific classes */
.won-text {
	/* Base styles for "YOU WON" text */
	position: absolute;
	top: 149px;
	left: 0;
	right: 0;
	text-align: center;
	color: #fff;
	font-family: 'IBM Plex Sans';
	font-size: 16px;
	font-style: normal;
	font-weight: 700;
	line-height: 120%; /* 19.2px */
	text-transform: uppercase;
}

.prize-text {
	/* Base styles for prize amount */
	position: absolute;
	top: 165px;
	left: 0;
	right: 0;
	text-align: center;
}

.eur-value {
	color: #fff;
	font-family: Cairo;
	font-size: 42px;
	font-style: normal;
	font-weight: 900;
	line-height: 100%; /* 42px */
	letter-spacing: -1.47px;
}

.eur-eur {
	color: #fff;
	font-family: Cairo;
	font-size: 16px;
	font-style: normal;
	font-weight: 900;
	line-height: 100%; /* 16px */
	text-transform: uppercase;
}

.local-currency {
	color: #fff;
	text-align: center;
	font-family: Cairo;
	font-size: 16px;
	font-style: normal;
	font-weight: 900;
	line-height: 100%; /* 16px */
	text-transform: uppercase;
}

.local-currency-tip {
	color: #fff;
	text-align: center;
	font-family: 'IBM Plex Sans';
	font-size: 12px;
	font-style: normal;
	font-weight: 600;
	line-height: 120%; /* 14.4px */
}

.prize-tip-text {
	/* Base styles for "72 hours" text */
	position: absolute;
	top: 253px;
	left: 0;
	right: 0;
	text-align: center;
	color: #fff;
	font-family: 'IBM Plex Sans';
	font-size: 10px;
	font-style: normal;
	font-weight: 600;
	line-height: 120%; /* 12px */
}

.conf-btn {
	/* Base styles for button */
	position: absolute;
	top: 316px;
	left: 0;
	right: 0;
	margin: 0 auto;
	border-radius: 10px;
	background: linear-gradient(180deg, #feeb33 0%, #fea904 100%);
	box-shadow:
		0px 2px 0px 0px #df6309,
		0px 4px 0px 0px #bd4c00,
		0px 10px 25px 0px rgba(48, 36, 96, 0.6);
	color: #000;
	text-align: center;
	font-family: 'IBM Plex Sans';
	font-size: 16px;
	font-style: normal;
	font-weight: 700;
	line-height: 120%; /* 19.2px */
	text-transform: uppercase;
	padding: 0.75rem 1.5rem;
	width: 80%;
	max-width: 250px;
	height: 3rem;
	cursor: pointer;
	transition: all 0.3s ease;
}

.conf-btn:hover {
	transform: translateY(-2px);
	box-shadow:
		0px 4px 0px 0px #df6309,
		0px 6px 0px 0px #bd4c00,
		0px 12px 28px 0px rgba(48, 36, 96, 0.7);
}

.conf-btn:active {
	transform: translateY(2px);
	box-shadow:
		0px 1px 0px 0px #df6309,
		0px 2px 0px 0px #bd4c00,
		0px 6px 15px 0px rgba(48, 36, 96, 0.5);
}

.campaign-title {
	/* Base styles for campaign title */
	position: absolute;
	top: 374px;
	left: 0;
	right: 0;
	text-align: center;
	color: #fff;
	font-family: 'IBM Plex Sans';
	font-size: 16px;
	font-style: normal;
	font-weight: 600;
	line-height: 120%;
	text-transform: uppercase;
}

.logo {
	/* Base styles for logo */
	position: absolute;
	top: 400px;
	left: 0;
	right: 0;
	text-align: center;
	display: flex;
	justify-content: center;
	width: 100%;
}

/* Responsive sizing for both viewport and popup container */
/* Medium screens (400px - 1200px) */
@media (min-width: 1920px) and (max-width: 2309px) {
	.viewport {
		width: calc((324px * 1.5) + 16px); /* Adjusted width + scrollbar width + padding */
	}

	.popup-container {
		min-width: calc(324px * 1.5);
		min-height: calc(504px * 1.5);
		max-height: 90vh;
	}

	/* Scale specific elements for medium screens */
	.won-text {
		top: calc(149px * 1.5);
		font-size: calc(16px * 1.5);
	}

	.eur-value {
		font-size: calc(42px * 1.5);
		letter-spacing: calc(-1.47px * 1.5);
	}

	.eur-eur {
		font-size: calc(16px * 1.5);
	}

	.prize-text {
		top: calc(165px * 1.5);
	}

	.prize-tip-text {
		top: calc(253px * 1.5);
		font-size: calc(10px * 1.5);
	}

	.conf-btn {
		top: calc(316px * 1.5);
		height: calc(3rem * 1.5);
		max-width: calc(250px * 1.5);
		font-size: calc(1.25rem * 1.5);
	}

	.logo {
		top: calc(400px * 1.5);
		height: calc(1.5rem * 1.5);
	}

	.campaign-title {
		top: calc(374px * 1.5);
		font-size: calc(14px * 1.5);
	}

	.local-currency {
		font-size: calc(16px * 1.5);
	}

	.local-currency-tip {
		font-size: calc(12px * 1.5);
		line-height: 120%;
	}
}

/* Large screens (1200px and more) */
@media (min-width: 2400px) {
	.viewport {
		width: calc((324px * 3) + 16px); /* Adjusted width + scrollbar width + padding */
	}

	.popup-container {
		min-width: calc(324px * 3);
		min-height: calc(504px * 3);
		max-height: 90vh;
	}

	/* Scale specific elements for large screens */
	.won-text {
		top: calc(149px * 3);
		font-size: calc(16px * 3);
	}

	.eur-value {
		font-size: calc(42px * 3);
		letter-spacing: calc(-1.47px * 3);
	}

	.eur-eur {
		font-size: calc(16px * 3);
	}

	.prize-text {
		top: calc(165px * 3);
	}

	.prize-tip-text {
		top: calc(253px * 3);
		font-size: calc(10px * 3);
	}

	.conf-btn {
		top: calc(316px * 3);
		height: calc(3rem * 3);
		max-width: calc(250px * 3);
		font-size: calc(1.25rem * 3);
	}

	.logo {
		top: calc(400px * 3);
		height: calc(1.5rem * 3);
	}

	.campaign-title {
		top: calc(374px * 3);
		font-size: calc(14px * 3);
	}

	.local-currency {
		font-size: calc(16px * 3);
	}

	.local-currency-tip {
		font-size: calc(12px * 3);
		line-height: 120%;
	}
}

/* Fade delayed transition */
.fade-delayed-enter-active {
	transition: all 1s ease;
	transition-delay: 0.8s;
	animation: glow 1s ease-in-out 0.8s;
}

.fade-delayed-leave-active {
	transition: opacity 0.5s ease;
}

.fade-delayed-enter-from,
.fade-delayed-leave-to {
	opacity: 0;
}

/* Slide down transition */
.slide-down-enter-active {
	transition: all 1.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	transition-delay: 0.4s;
	animation: glow 1.2s ease-in-out 0.4s;
}

.slide-down-leave-active {
	transition: all 0.7s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
	opacity: 0.1;
	transform: translateY(-60px);
}

/* Slide up transition */
.slide-up-enter-active {
	transition: all 1.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	transition-delay: 1.2s;
	animation: glow 1.2s ease-in-out 1.2s;
}

.slide-up-leave-active {
	transition: all 0.7s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
	opacity: 0.1;
	transform: translateY(60px);
}

/* Scale up transition */
.scale-up-enter-active {
	transition: all 1.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	transition-delay: 2s;
	animation: buttonGlow 1.2s ease-in-out 2s;
}

.scale-up-leave-active {
	transition: all 0.7s ease;
}

.scale-up-enter-from,
.scale-up-leave-to {
	opacity: 0.1;
	transform: scale(0.7);
}

/* Pulsing shadow for button */
.pulse-shadow {
	animation: pulseShadow 2.5s infinite;
	animation-delay: 3s;
}

@keyframes pulseShadow {
	0%,
	100% {
		box-shadow:
			0 0 0 rgba(59, 130, 246, 0.5),
			inset 0 -4px 0 0 rgba(0, 0, 0, 0.3),
			inset 0 1px 1px 0 rgba(255, 255, 255, 0.35);
		transform: scale(1);
	}
	50% {
		box-shadow:
			0 0 25px rgba(59, 130, 246, 1),
			inset 0 -4px 0 0 rgba(0, 0, 0, 0.3),
			inset 0 1px 1px 0 rgba(255, 255, 255, 0.35);
		transform: scale(1.07);
	}
}

@keyframes glow {
	0% {
		text-shadow: 0 0 0 rgba(255, 255, 255, 0);
	}
	50% {
		text-shadow: 0 0 15px rgba(255, 255, 255, 0.8);
	}
	100% {
		text-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
	}
}

@keyframes buttonGlow {
	0% {
		box-shadow: 0 0 0 rgba(59, 130, 246, 0);
	}
	50% {
		box-shadow: 0 0 30px rgba(59, 130, 246, 1);
	}
	100% {
		box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
	}
}
</style>
